import { createRouter } from "@openclaw/uirouter";
import type {
  PageDefinition,
  RouteLocation,
  RouteMatch,
  RouteNotFound,
  Router,
  RouterHistory,
} from "@openclaw/uirouter";
import {
  agentRouteFromPath,
  canonicalPluginTabLocation,
  dynamicRouteFromPath,
  isSessionRouteId,
  pathForAgentPanel,
  pathForRoute,
  pluginSlugCandidate,
  pluginTabSlugFromPath,
  routeIdFromPath,
  setPluginTabSlugs,
  type RouteId,
} from "./app-route-paths.ts";
import type { ApplicationContext } from "./app/context.ts";
import { gatewayPresentationScope } from "./app/gateway-presentation-scope.ts";
import { page as aboutPage } from "./pages/about/route.ts";
import { page as activityPage } from "./pages/activity/route.ts";
import { page as agentsHomePage } from "./pages/agents-home/route.ts";
import { page as agentsPage } from "./pages/agents/route.ts";
import { page as approvalsPage } from "./pages/approvals/route.ts";
import { page as appsPage } from "./pages/apps/route.ts";
import { page as channelsPage } from "./pages/channels/route.ts";
import { pages as chatPages } from "./pages/chat/route.ts";
import { page as cloudWorkersPage } from "./pages/cloud-workers/route.ts";
import { pages as configPages } from "./pages/config/route.ts";
import { page as connectionPage } from "./pages/connection/route.ts";
import { page as cronPage } from "./pages/cron/route.ts";
import { page as custodianPage } from "./pages/custodian/route.ts";
import { page as dashboardsPage } from "./pages/dashboards/route.ts";
import { page as debugPage } from "./pages/debug/route.ts";
import {
  page as devicePage,
  permissionsPage as devicePermissionsPage,
} from "./pages/device/route.ts";
import { page as devicesPage } from "./pages/devices/route.ts";
import { page as labsPage } from "./pages/labs/route.ts";
import { page as lobsterdexPage } from "./pages/lobsterdex/route.ts";
import { page as logsPage } from "./pages/logs/route.ts";
import { page as meetingsPage } from "./pages/meetings/route.ts";
import { page as memoryImportPage } from "./pages/memory-import/route.ts";
import { page as modelProvidersPage } from "./pages/model-providers/route.ts";
import { page as modelSetupPage } from "./pages/model-setup/route.ts";
import { page as newSessionPage } from "./pages/new-session/route.ts";
import { page as pluginPage } from "./pages/plugin/route.ts";
import { pages as pluginsPages } from "./pages/plugins/route.ts";
import { page as portalsPage } from "./pages/portals/route.ts";
import { page as profilePage } from "./pages/profile/route.ts";
import { page as secretsPage } from "./pages/secrets/route.ts";
import { page as sessionsPage } from "./pages/sessions/route.ts";
import { page as skillWorkshopPage } from "./pages/skill-workshop/route.ts";
import { pages as skillsPages } from "./pages/skills/route.ts";
import { page as tasksPage } from "./pages/tasks/route.ts";
import { page as terminalPage } from "./pages/terminal/route.ts";
import { page as usagePage } from "./pages/usage/route.ts";
import { resolveWorkboardRouteLocation } from "./pages/workboard/route-location.ts";
import { page as workboardPage } from "./pages/workboard/route.ts";
import { page as worktreesPage } from "./pages/worktrees/route.ts";

type AppRouteModule = {
  render: (data: unknown, loaderPending: boolean, presented?: boolean) => unknown;
  retainOnNavigate?: boolean;
  renderOwnerKey?: (
    match: Pick<RouteMatch, "data" | "location">,
    settled: Pick<RouteMatch, "data" | "location"> | undefined,
  ) => string | undefined;
};

export type ApplicationRouter = Router<
  RouteId,
  ApplicationContext<RouteId>,
  AppRouteModule,
  unknown
>;
type AppRoute = PageDefinition<RouteId, ApplicationContext<RouteId>, AppRouteModule>;

const APP_ROUTE_TREE = [
  ...chatPages,
  custodianPage,
  newSessionPage,
  terminalPage,
  activityPage,
  meetingsPage,
  dashboardsPage,
  appsPage,
  portalsPage,
  agentsHomePage,
  agentsPage,
  approvalsPage,
  channelsPage,
  cloudWorkersPage,
  connectionPage,
  labsPage,
  aboutPage,
  lobsterdexPage,
  ...configPages,
  modelSetupPage,
  modelProvidersPage,
  memoryImportPage,
  profilePage,
  workboardPage,
  worktreesPage,
  sessionsPage,
  secretsPage,
  usagePage,
  debugPage,
  logsPage,
  skillWorkshopPage,
  ...skillsPages,
  ...pluginsPages,
  cronPage,
  tasksPage,
  devicePage,
  devicePermissionsPage,
  devicesPage,
  pluginPage,
] as const;

const appRoutes = APP_ROUTE_TREE as readonly AppRoute[];

/** Starts route chunk downloads without running the route's loader. */
export function warmApplicationRouteModule(
  router: ApplicationRouter,
  location: RouteLocation,
  basePath: string,
): void {
  const routeId = routeIdFromPath(location.pathname, basePath);
  const route = routeId ? router.getRoute(routeId) : null;
  if (route) {
    // Navigation owns chunk errors; its import reuses the browser's module cache.
    void Promise.resolve(route.component()).catch(() => undefined);
  }
}

function canonicalRouteLocation(
  routeId: RouteId | null,
  location: RouteLocation,
  basePath: string,
): RouteLocation {
  return routeId === "workboard"
    ? (resolveWorkboardRouteLocation(location, basePath).canonicalLocation ?? location)
    : routeId === "plugin"
      ? canonicalPluginTabLocation(location, basePath)
      : location;
}

export function createApplicationRouter(): ApplicationRouter {
  const router = createRouter<RouteId, ApplicationContext<RouteId>, AppRouteModule>({
    routes: appRoutes,
  });
  // The shared router intentionally matches exact paths only. People, Workboard
  // ids, hub tabs, and session refs are runtime data, so the app owns those paths.
  return {
    ...router,
    navigate: (routeId, context, options, location) =>
      router.navigate(
        routeId,
        context,
        options,
        location ? canonicalRouteLocation(routeId, location, context.basePath) : undefined,
      ),
    routeIdFromPath,
  };
}

function routerHistoryLocation(location: ReturnType<RouterHistory["location"]>, basePath: string) {
  const dynamicRoute = dynamicRouteFromPath(location.pathname, basePath);
  if (!dynamicRoute) {
    return location;
  }
  const [routeId, searchKey, searchValue] = dynamicRoute;
  const search = new URLSearchParams(location.search);
  search.set(searchKey, searchValue);
  return {
    ...location,
    pathname: pathForRoute(routeId, basePath),
    search: `?${search.toString()}`,
  };
}

export function sameRouteLocation(left: RouteLocation, right: RouteLocation): boolean {
  return (
    left.pathname === right.pathname && left.search === right.search && left.hash === right.hash
  );
}

function isRouteNotFound(error: unknown): error is RouteNotFound {
  return (
    typeof error === "object" && error !== null && "type" in error && error.type === "notFound"
  );
}

async function tolerateRouteNotFound(navigation: Promise<void>): Promise<void> {
  try {
    await navigation;
  } catch (error) {
    // uirouter commits not-found state before rethrowing; the outlet owns its recovery UI.
    if (!isRouteNotFound(error)) {
      throw error;
    }
  }
}

export async function startApplicationRouter(
  router: ApplicationRouter,
  history: RouterHistory,
  basePath: string,
  context: ApplicationContext<RouteId>,
): Promise<void> {
  setPluginTabSlugs(context.gateway.snapshot.hello?.controlUiTabs);
  let location = history.location();
  const canonicalLocation = canonicalRouteLocation(
    routeIdFromPath(location.pathname, basePath),
    location,
    basePath,
  );
  // Normalize the requested URL before loaders or preload caches can outlive it.
  if (!sameRouteLocation(location, canonicalLocation)) {
    history.replace(canonicalLocation);
    location = history.location();
  }
  const initialAgentRoute = agentRouteFromPath(location.pathname, basePath);
  if (initialAgentRoute?.invalidPanel) {
    history.replace({
      ...location,
      pathname: pathForAgentPanel(initialAgentRoute.agentId, null, basePath),
    });
    location = history.location();
  }
  // Single-segment plugin deep links wait for hello before outlet recovery.
  if (
    routeIdFromPath(location.pathname, basePath) === null &&
    !pluginSlugCandidate(location.pathname, basePath)
  ) {
    history.replace({
      ...location,
      pathname: router.pathForRoute("chat", basePath),
    });
    location = history.location();
  }
  const initialDynamicRoute = dynamicRouteFromPath(location.pathname, basePath);
  const applicationHistory: RouterHistory = {
    location: () => routerHistoryLocation(history.location(), basePath),
    push: (next) => history.push(next),
    replace: (next) => history.replace(next),
    listen: (listener) => {
      let listening = true;
      let recoveryQueued = false;
      let interrupted:
        | { controller: AbortController; scope: ReturnType<typeof gatewayPresentationScope> }
        | undefined;
      const currentTarget = () => {
        const state = router.getState();
        return state.pendingMatches[0] ?? state.matches[0];
      };
      const recoverSessionRoute = () => {
        const target = currentTarget();
        if (!target || !isSessionRouteId(target.routeId)) {
          interrupted = undefined;
          return;
        }
        const scope = gatewayPresentationScope(context.gateway);
        if (interrupted?.controller !== target.abortController) {
          interrupted = undefined;
        }
        if (interrupted && interrupted.scope !== scope) {
          return;
        }
        if (context.gateway.snapshot.phase !== "connected") {
          if (target.status === "pending" || target.isFetching === "loader") {
            interrupted = { controller: target.abortController, scope };
          }
          return;
        }
        if (target.status === "success" && !target.isFetching) {
          interrupted = undefined;
        }
        if (!interrupted || recoveryQueued || target.status !== "error") {
          return;
        }
        recoveryQueued = true;
        // Other subscribers may navigate synchronously; recover only their final intent.
        queueMicrotask(() => {
          recoveryQueued = false;
          const latest = currentTarget();
          if (
            !listening ||
            !interrupted ||
            latest?.abortController !== interrupted.controller ||
            gatewayPresentationScope(context.gateway) !== interrupted.scope ||
            context.gateway.snapshot.phase !== "connected" ||
            latest.status !== "error"
          ) {
            return;
          }
          interrupted = undefined;
          // The loader publishes its error before retiring its run. Abort it so
          // same-match revalidation cannot join the already failed promise.
          latest.abortController.abort();
          if (currentTarget()?.abortController !== latest.abortController) {
            return;
          }
          void router
            .navigate(
              latest.routeId,
              context,
              { history: "none", revalidate: true },
              latest.location,
            )
            .catch(() => undefined);
        });
      };
      const stopSessionRecovery = router.subscribe(recoverSessionRoute);
      let lastHello = context.gateway.snapshot.hello;
      const stopGateway = context.gateway.subscribe((snapshot) => {
        recoverSessionRoute();
        if (lastHello === snapshot.hello) {
          return;
        }
        lastHello = snapshot.hello;
        setPluginTabSlugs(snapshot.hello?.controlUiTabs);
        queueMicrotask(() => {
          if (!listening || context.gateway.snapshot.phase !== "connected") {
            return;
          }
          const current = history.location();
          const canonical = canonicalPluginTabLocation(current, basePath);
          const state = router.getState();
          if (state.pendingMatches.some((match) => !sameRouteLocation(match.location, current))) {
            return;
          }
          const slugRoute =
            current.pathname !== pathForRoute("plugin", basePath) &&
            [...state.matches, ...state.pendingMatches].some((match) => match.routeId === "plugin");
          if (
            !sameRouteLocation(current, canonical) ||
            (slugRoute && pluginTabSlugFromPath(current.pathname, basePath))
          ) {
            void router
              .navigate("plugin", context, { history: "replace" }, canonical)
              .catch((error: unknown) => {
                console.error("[openclaw] Plugin tab navigation failed", error);
              });
          } else if (slugRoute) {
            listener(current);
          }
        });
      });
      const stopHistory = history.listen((next) => {
        const canonical = canonicalRouteLocation(
          routeIdFromPath(next.pathname, basePath),
          next,
          basePath,
        );
        if (!sameRouteLocation(next, canonical)) {
          history.replace(canonical);
        }
        const dynamicRoute = dynamicRouteFromPath(canonical.pathname, basePath);
        if (dynamicRoute) {
          void router
            .navigate(dynamicRoute[0], context, { history: "none" }, canonical)
            .catch((error: unknown) => {
              console.error("[openclaw] Dynamic route navigation failed", error);
            });
          return;
        }
        listener(canonical);
      });
      return () => {
        listening = false;
        interrupted = undefined;
        stopSessionRecovery();
        stopGateway();
        stopHistory();
      };
    },
  };
  await tolerateRouteNotFound(router.start(applicationHistory, basePath, context));
  if (initialDynamicRoute && sameRouteLocation(history.location(), location)) {
    // Replace the synthetic exact-match location with the real browser path
    // before the shell renders. A loader-visible redirect wins if it already
    // moved history while startup was still resolving.
    await tolerateRouteNotFound(
      router.navigate(initialDynamicRoute[0], context, { history: "none" }, location),
    );
  }
}

export {
  APP_ROUTE_IDS,
  isRouteId,
  locationForRoute,
  routeIdFromPath,
  type RouteId,
} from "./app-route-paths.ts";
